/**
 * Fuzzy Matching Utility for Fill-in-the-Blank Questions
 * 
 * Provides forgiving grading for elementary students:
 * - Case-insensitive comparison
 * - Trimmed whitespace
 * - Fuzzy matching with ~85% similarity tolerance
 * - Synonym support for multiple correct answers
 */

/**
 * Calculate Levenshtein distance between two strings
 * @param {string} a - First string
 * @param {string} b - Second string
 * @returns {number} - Edit distance
 */
function levenshteinDistance(a, b) {
  const matrix = [];
  
  // Initialize matrix
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }
  
  // Fill matrix
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }
  
  return matrix[b.length][a.length];
}

/**
 * Calculate similarity ratio between two strings (0-1)
 * @param {string} a - First string
 * @param {string} b - Second string
 * @returns {number} - Similarity ratio (0 = completely different, 1 = identical)
 */
function similarityRatio(a, b) {
  if (!a && !b) return 1;
  if (!a || !b) return 0;
  
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  
  const distance = levenshteinDistance(a, b);
  return 1 - (distance / maxLen);
}

/**
 * Normalize a string for comparison
 * - Lowercase
 * - Trim whitespace
 * - Remove extra spaces
 * - Remove common punctuation
 */
function normalizeForComparison(str) {
  if (!str) return '';
  return str
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')           // Collapse multiple spaces
    .replace(/[.,!?;:'"()-]/g, '')  // Remove punctuation
    .trim();
}

/**
 * Check if user answer matches the correct answer with fuzzy tolerance
 * 
 * @param {string} userAnswer - The student's answer
 * @param {string|string[]} correctAnswer - The correct answer(s) - can be string or array of synonyms
 * @param {Object} options - Grading options
 * @param {number} options.threshold - Similarity threshold (default: 0.85 = 85%)
 * @param {boolean} options.allowPartialCredit - If true, very close answers get partial credit
 * @returns {Object} - { isCorrect: boolean, similarity: number, matchedAnswer: string|null }
 */
export function fuzzyMatchAnswer(userAnswer, correctAnswer, options = {}) {
  const threshold = options.threshold ?? 0.85;  // 85% similarity required
  
  // Normalize user answer
  const normalizedUser = normalizeForComparison(userAnswer);
  
  // Handle array of correct answers (synonyms)
  const correctAnswers = Array.isArray(correctAnswer) ? correctAnswer : [correctAnswer];
  
  let bestMatch = {
    isCorrect: false,
    similarity: 0,
    matchedAnswer: null,
    details: []
  };
  
  for (const answer of correctAnswers) {
    const normalizedCorrect = normalizeForComparison(answer);
    
    // Skip empty answers
    if (!normalizedCorrect) continue;
    
    // Calculate similarity
    const similarity = similarityRatio(normalizedUser, normalizedCorrect);
    
    // Track this comparison
    bestMatch.details.push({
      correctAnswer: answer,
      similarity: similarity,
      meetsThreshold: similarity >= threshold
    });
    
    // Check if this is the best match so far
    if (similarity > bestMatch.similarity) {
      bestMatch.similarity = similarity;
      bestMatch.matchedAnswer = answer;
      bestMatch.isCorrect = similarity >= threshold;
    }
    
    // Perfect match - no need to check more
    if (similarity === 1) break;
  }
  
  return bestMatch;
}

/**
 * Common synonyms for elementary education topics
 * This can be expanded based on subject matter
 */
export const COMMON_SYNONYMS = {
  // Science
  'photosynthesis': ['photosynthesis'],
  'chloroplast': ['chloroplast', 'chloroplasts'],
  'gravity': ['gravity', 'gravitational force', 'gravitational pull'],
  'orbit': ['orbit', 'orbital path'],
  'digestive': ['digestive', 'digestion'],
  'atmosphere': ['atmosphere', 'air'],
  'evaporation': ['evaporation', 'evaporating'],
  'condensation': ['condensation', 'condensating'],
  'precipitation': ['precipitation', 'rain'],
  
  // Math terms
  'numerator': ['numerator', 'top number'],
  'denominator': ['denominator', 'bottom number'],
  'quotient': ['quotient', 'answer', 'result'],
  'product': ['product', 'answer', 'result'],
  'sum': ['sum', 'total', 'answer'],
  'difference': ['difference', 'result'],
  
  // Social Studies
  'democracy': ['democracy', 'democratic'],
  'constitution': ['constitution'],
  'president': ['president'],
  'congress': ['congress', 'legislature'],
};

/**
 * Get synonyms for a correct answer
 * @param {string} answer - The correct answer
 * @returns {string[]} - Array of acceptable answers including synonyms
 */
export function getSynonyms(answer) {
  const normalized = normalizeForComparison(answer);
  
  // Check if we have predefined synonyms
  for (const [key, synonyms] of Object.entries(COMMON_SYNONYMS)) {
    if (normalizeForComparison(key) === normalized || 
        synonyms.some(s => normalizeForComparison(s) === normalized)) {
      return synonyms;
    }
  }
  
  // No synonyms found, return original answer
  return [answer];
}

/**
 * Grade a fill-in-the-blank answer with full fuzzy matching support
 * 
 * @param {string} userAnswer - Student's answer
 * @param {string} correctAnswer - The correct answer from the question
 * @param {string[]} alternativeAnswers - Optional array of alternative correct answers
 * @returns {Object} - Grading result
 */
export function gradeFillBlankAnswer(userAnswer, correctAnswer, alternativeAnswers = []) {
  // Build list of all acceptable answers
  const allAcceptableAnswers = [
    correctAnswer,
    ...getSynonyms(correctAnswer),
    ...alternativeAnswers
  ].filter(Boolean);
  
  // Remove duplicates
  const uniqueAnswers = [...new Set(allAcceptableAnswers.map(a => a.toLowerCase()))];
  const originalAnswers = allAcceptableAnswers.filter((a, i) => 
    uniqueAnswers.indexOf(a.toLowerCase()) === allAcceptableAnswers.findIndex(b => b.toLowerCase() === a.toLowerCase())
  );
  
  // Perform fuzzy matching
  const result = fuzzyMatchAnswer(userAnswer, originalAnswers, { threshold: 0.85 });
  
  // Log for debugging
  console.log(`[TEI_RENDERER] fill_blank fuzzy grading:`, {
    userAnswer,
    correctAnswer,
    alternativeAnswers: originalAnswers,
    similarity: (result.similarity * 100).toFixed(1) + '%',
    threshold: '85%',
    isCorrect: result.isCorrect,
    matchedAnswer: result.matchedAnswer
  });
  
  return result;
}

export default fuzzyMatchAnswer;
